import { latLngToLocalMeters, roadWidthMeters, isDrivable } from '@roadworld/map-generator';
import type { ChunkPayload, OsmWay, RoadClass } from '@roadworld/shared';
import type { HeightfieldData, MeshGeometry } from './geometry.js';
import { sampleHeightfield } from './heightfield.js';
import { buildSmoothedCenterline, type CenterlineSample } from './roadCenterline.js';

/** Vertical clearance for bridges above terrain. Just enough to read as elevated without
 *  making the approach ramp feel like a jump. */
const BRIDGE_DECK_LIFT = 3;
/** Length over which the bridge deck ramps from terrain height up to full lift. The car
 *  drives up this ramp instead of hitting a vertical step at the abutment. */
const BRIDGE_RAMP_LENGTH = 12;
/** Square footprint of each drivable-bridge abutment pillar. */
const PILLAR_FOOTPRINT_DRIVABLE = 1.5;
/** Path bridges are thinner — proportionally smaller pillar so it doesn't dominate the deck. */
const PILLAR_FOOTPRINT_PATH = 0.9;
/** Tiny gap between pillar top and the deck strip. Just enough to avoid z-fighting (the
 *  deck is a single coplanar strip with no thickness), but small enough to be invisible at
 *  driving distance. */
const PILLAR_TOP_GAP_BELOW_DECK = 0.1;
/** Pillars are anchored at the deck and extend straight down this far. The visible portion
 *  goes from terrain to deck; anything below terrain is hidden inside the heightfield. This
 *  works whether the bridge is over flat ground or spans a deep depression — we never need
 *  to know where the ground actually is to draw a correct pillar. */
const PILLAR_DEPTH_BELOW_TOP = 20;
/** Height of the visual guardrail above the bridge deck. Thin single-panel mesh; no collider. */
const GUARDRAIL_HEIGHT = 0.55;
/** Width multiplier for path/footway bridges. Default path widths are tuned for pedestrians;
 *  bridges need to be wide enough that a car can drive across. Applied as a taper from the
 *  abutment (1.0×) to the deck shoulder (full multiplier). */
const PATH_BRIDGE_WIDTH_MULTIPLIER = 1.6;

export interface RoadMeshOptions {
  /** Y lift for drivable roads above terrain. Above paths so roads occlude path geometry. */
  liftDrivable: number;
  /** Y lift for pedestrian paths above terrain. Below roads so paths get hidden under them. */
  liftPath: number;
  /** Max distance between centerline samples in meters. Inserted samples follow terrain. */
  subdivideMeters: number;
}

export interface RoadMeshGroup {
  /** Drivable roads (cars). */
  drivable: MeshGeometry;
  /** Pedestrian/footway paths. */
  paths: MeshGeometry;
  /** Bridge-only drivable geometry (subset of `drivable`). Use this to drive a trimesh
   *  collider so the car can ramp up onto bridges; regular roads ride on the heightfield. */
  bridges: MeshGeometry;
}

export function buildRoadMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
  options: RoadMeshOptions = { liftDrivable: 0.1, liftPath: 0.05, subdivideMeters: 3 },
): RoadMeshGroup {
  const drivableWays: OsmWay[] = [];
  const pathWays: OsmWay[] = [];
  for (const way of payload.osm.ways) {
    (isDrivable(way.roadClass) ? drivableWays : pathWays).push(way);
  }
  // All bridges — drivable AND path — get a collider so the car can drive across either.
  // Path bridges are intentionally widened in appendWayMesh so a car can fit.
  const bridgeWays = [...drivableWays, ...pathWays].filter((w) => w.bridge && !w.tunnel);

  return {
    drivable: buildGroup(payload, drivableWays, heightfield, options.liftDrivable, options.subdivideMeters),
    paths: buildGroup(payload, pathWays, heightfield, options.liftPath, options.subdivideMeters),
    // Use liftDrivable for all bridges so the collider sits a hair above the visual path
    // (~0.05 m) — invisible at driving distance, keeps wheels from clipping through.
    bridges: buildGroup(payload, bridgeWays, heightfield, options.liftDrivable, options.subdivideMeters),
  };
}

function buildGroup(
  payload: ChunkPayload,
  ways: OsmWay[],
  heightfield: HeightfieldData,
  lift: number,
  subdivideMeters: number,
): MeshGeometry {
  const positionsArr: number[] = [];
  const indicesArr: number[] = [];
  const normalsArr: number[] = [];
  const uvsArr: number[] = [];

  for (const way of ways) {
    appendWayMesh(payload, way, heightfield, lift, subdivideMeters, positionsArr, indicesArr, normalsArr, uvsArr);
  }

  return {
    positions: new Float32Array(positionsArr),
    indices: new Uint32Array(indicesArr),
    normals: new Float32Array(normalsArr),
    uvs: new Float32Array(uvsArr),
  };
}

function appendWayMesh(
  payload: ChunkPayload,
  way: OsmWay,
  heightfield: HeightfieldData,
  lift: number,
  subdivideMeters: number,
  positions: number[],
  indices: number[],
  normals: number[],
  uvs: number[],
): void {
  // Tunnels live below the surface — no surface mesh. The car can't drive through them in
  // this prototype, but rendering a flat ribbon over an underground road would look wrong.
  if (way.tunnel) return;

  const baseWidth = roadWidthMeters(way.roadClass);
  // Path bridges taper from base width at the abutment to (base × multiplier) on the deck.
  // The taper uses the same in/out ramp as the deck-height lift, so width and height grow
  // together — the bridge "expands" smoothly out of the connecting path.
  const isPathBridge = way.bridge === true && !isDrivable(way.roadClass);
  const widthBoostAtDeck = isPathBridge ? PATH_BRIDGE_WIDTH_MULTIPLIER : 1;

  // Smoothed (Catmull-Rom) centerline, terrain-sampled, with cumulative arc length and
  // tangent/perpendicular precomputed per sample. Same helper is reused by markings,
  // shoulders, curbs and bollards so everything aligns to the same curve.
  const centerline = buildSmoothedCenterline(payload, way, heightfield, lift, subdivideMeters);
  if (!centerline || centerline.length < 2) return;

  // Bridge: re-shape the centerline so the deck ramps up smoothly from the abutments
  // (which stay at terrain height) to a flat elevated span in the middle. The car can
  // drive up the ramp instead of hitting a vertical step. For path bridges, also taper the
  // width along the same ramp so the bridge widens out of the connecting path.
  if (way.bridge) {
    applyBridgeRamp(centerline, widthBoostAtDeck);
  }

  // Compute per-vertex edge offsets perpendicular to the segment direction.
  const baseIndex = positions.length / 3;
  for (let i = 0; i < centerline.length; i++) {
    const curr = centerline[i]!;
    const v = curr.distance / Math.max(1, baseWidth);

    // Per-vertex width: baseWidth × current point's widthBoost. For non-bridges and drivable
    // bridges widthBoost is always 1; only path bridges taper width along the ramp.
    const halfWidth = baseWidth * curr.widthBoost * 0.5;

    // Sample terrain at each edge's actual (x, z) so the ribbon banks with cross-slope
    // instead of using the centerline Y everywhere. Without this, on side-slopes the road
    // edges sit at the wrong Y relative to terrain — and a path crossing the road can
    // end up above the road even when its lift is smaller. For bridges we deliberately
    // ignore the terrain and use the (already-interpolated) centerline height so the deck
    // stays flat across the span.
    const leftX = curr.x + curr.perpX * halfWidth;
    const leftZ = curr.z + curr.perpZ * halfWidth;
    const leftY = way.bridge ? curr.h : sampleHeightfield(heightfield, leftX, leftZ) + lift;
    positions.push(leftX, leftY, leftZ);
    normals.push(0, 1, 0);
    uvs.push(0, v);

    const rightX = curr.x - curr.perpX * halfWidth;
    const rightZ = curr.z - curr.perpZ * halfWidth;
    const rightY = way.bridge ? curr.h : sampleHeightfield(heightfield, rightX, rightZ) + lift;
    positions.push(rightX, rightY, rightZ);
    normals.push(0, 1, 0);
    uvs.push(1, v);
  }

  // Triangulate as a strip.
  for (let i = 0; i < centerline.length - 1; i++) {
    const a = baseIndex + i * 2;
    const b = baseIndex + i * 2 + 1;
    const c = baseIndex + (i + 1) * 2;
    const d = baseIndex + (i + 1) * 2 + 1;
    indices.push(a, c, b, b, c, d);
  }
}

export function roadColor(roadClass: RoadClass): [number, number, number] {
  if (roadClass === 'track') return [0.42, 0.34, 0.24];
  if (roadClass === 'footway' || roadClass === 'path' || roadClass === 'pedestrian') {
    return [0.55, 0.5, 0.42];
  }
  if (roadClass === 'cycleway') return [0.3, 0.35, 0.45];
  return [0.18, 0.18, 0.2];
}

/**
 * Cuboid pillars at each bridge abutment-shoulder (one BRIDGE_RAMP_LENGTH inside each end,
 * where the deck has just reached full lift). Visual-only — no physics collider.
 *
 * The pillar's top is computed to actually meet the deck: it uses the same linear-interp
 * baseline between the two abutments as the deck mesh, plus the bridge's own road lift, so
 * thin path-bridge pillars don't poke proud of their thinner deck and chunky drivable
 * pillars sit flush under their thicker deck.
 */
export function buildBridgePillarMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): MeshGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];

  for (const way of payload.osm.ways) {
    if (!way.bridge || way.tunnel) continue;
    if (way.nodes.length < 2) continue;

    const pts: Array<{ x: number; z: number }> = [];
    for (const nodeId of way.nodes) {
      const node = payload.osm.nodes[nodeId];
      if (!node) continue;
      pts.push(latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng));
    }
    if (pts.length < 2) continue;

    let totalLen = 0;
    for (let i = 1; i < pts.length; i++) {
      totalLen += Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.z - pts[i - 1]!.z);
    }
    // Skip bridges too short to have a fully-elevated section — the lift never reaches max,
    // so a full-height pillar would poke through the deck.
    if (totalLen < 2 * BRIDGE_RAMP_LENGTH) continue;

    // Road-lift baseline (matches the lift used when buildRoadMeshes built this bridge's
    // visible deck) and per-road-type footprint.
    const drivable = isDrivable(way.roadClass);
    const roadLift = drivable ? 0.1 : 0.05;
    const footprint = drivable ? PILLAR_FOOTPRINT_DRIVABLE : PILLAR_FOOTPRINT_PATH;
    const h0 = sampleHeightfield(heightfield, pts[0]!.x, pts[0]!.z) + roadLift;
    const hN = sampleHeightfield(heightfield, pts[pts.length - 1]!.x, pts[pts.length - 1]!.z) + roadLift;

    for (const shoulderDist of [BRIDGE_RAMP_LENGTH, totalLen - BRIDGE_RAMP_LENGTH]) {
      const p = pointAtArcLength(pts, shoulderDist);
      // Deck height at the shoulder, matching applyBridgeRamp: linear-interp the abutment
      // heights along the bridge, then add the full deck lift (liftFactor=1 on the shoulder).
      const t = shoulderDist / totalLen;
      const deckY = h0 + (hN - h0) * t + BRIDGE_DECK_LIFT;
      // Anchor the pillar TOP at the deck (with a hair of clearance to avoid z-fighting),
      // then extend downward into terrain. The visible portion runs from terrain up to the
      // deck for free; we don't need to know where terrain actually is at this point.
      const topY = deckY - PILLAR_TOP_GAP_BELOW_DECK;
      const bottomY = topY - PILLAR_DEPTH_BELOW_TOP;
      appendCuboid(positions, indices, normals, uvs, p.x, p.z, bottomY, topY, footprint);
    }
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
  };
}

/**
 * Thin visual-only guardrails along each side of drivable bridge decks. Single-quad strip
 * extruded up GUARDRAIL_HEIGHT from each deck edge. No physics collider — the user wanted a
 * weak-looking rail that the car can break through if it really tries.
 */
export function buildBridgeGuardrailMeshes(
  payload: ChunkPayload,
  heightfield: HeightfieldData,
): MeshGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];

  const subdivideMeters = 3;
  const lift = 0.1; // matches default liftDrivable in buildRoadMeshes

  for (const way of payload.osm.ways) {
    if (!way.bridge || way.tunnel) continue;
    if (!isDrivable(way.roadClass)) continue;

    // Re-derive the same smoothed centerline + ramp as the road mesh so the rail aligns
    // with the deck.
    const centerline = buildSmoothedCenterline(payload, way, heightfield, lift, subdivideMeters);
    if (!centerline || centerline.length < 2) continue;
    // Guardrails only run on drivable bridges (filtered above) — no width taper.
    applyBridgeRamp(centerline, 1);

    const halfWidth = roadWidthMeters(way.roadClass) * 0.5;

    // Emit two single-panel strips — one per side. Bottom verts sit on the deck so the rail
    // looks integral; top verts sit GUARDRAIL_HEIGHT above. Normal points outward (away from
    // road centerline) so external light catches it; backface culling is off on the material.
    for (const side of [1, -1] as const) {
      const base = positions.length / 3;
      for (const e of centerline) {
        const x = e.x + e.perpX * halfWidth * side;
        const z = e.z + e.perpZ * halfWidth * side;
        const nX = e.perpX * side;
        const nZ = e.perpZ * side;
        positions.push(x, e.h, z);
        normals.push(nX, 0, nZ);
        uvs.push(0, 0);
        positions.push(x, e.h + GUARDRAIL_HEIGHT, z);
        normals.push(nX, 0, nZ);
        uvs.push(0, 1);
      }
      for (let i = 0; i < centerline.length - 1; i++) {
        const a = base + i * 2;
        const b = base + i * 2 + 1;
        const c = base + (i + 1) * 2;
        const d = base + (i + 1) * 2 + 1;
        indices.push(a, c, b, b, c, d);
      }
    }
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    normals: new Float32Array(normals),
    uvs: new Float32Array(uvs),
  };
}

/**
 * Re-shape a centerline so the bridge deck ramps up from the abutments (terrain height) to
 * a flat elevated middle section, then ramps back down. The endpoints stay at terrain.
 *
 * If `widthBoostAtDeck` > 1, also taper the per-point `widthBoost` along the same in/out ramp:
 *   - widthBoost = 1 at the abutment (matches connecting path width)
 *   - widthBoost = widthBoostAtDeck on the flat deck (full boost)
 *   - linear interpolation in between
 * For widthBoostAtDeck === 1 (drivable bridges) the width stays constant.
 */
function applyBridgeRamp(
  centerline: CenterlineSample[],
  widthBoostAtDeck: number,
): void {
  const last = centerline.length - 1;
  if (last < 1) return;
  const cumDist = new Array(centerline.length).fill(0) as number[];
  for (let i = 1; i <= last; i++) {
    const dx = centerline[i]!.x - centerline[i - 1]!.x;
    const dz = centerline[i]!.z - centerline[i - 1]!.z;
    cumDist[i] = cumDist[i - 1]! + Math.hypot(dx, dz);
  }
  const totalLen = cumDist[last]!;
  if (totalLen <= 0) return;
  const h0 = centerline[0]!.h;
  const hN = centerline[last]!.h;
  // Cap ramp length at 40% of bridge per side so very short bridges still have a (smaller)
  // peak rather than overlapping ramps that fight each other.
  const rampLen = Math.min(BRIDGE_RAMP_LENGTH, totalLen * 0.4);
  const wantsWidthTaper = widthBoostAtDeck > 1;
  for (let i = 0; i <= last; i++) {
    const d = cumDist[i]!;
    const t = d / totalLen;
    const inFrac = rampLen > 0 ? Math.min(1, d / rampLen) : 1;
    const outFrac = rampLen > 0 ? Math.min(1, (totalLen - d) / rampLen) : 1;
    const liftFactor = Math.min(inFrac, outFrac);
    centerline[i]!.h = h0 + (hN - h0) * t + BRIDGE_DECK_LIFT * liftFactor;
    if (wantsWidthTaper) {
      centerline[i]!.widthBoost = 1 + (widthBoostAtDeck - 1) * liftFactor;
    }
  }
}

/** Return the (x, z) point at a given arc length along a polyline. */
function pointAtArcLength(
  pts: Array<{ x: number; z: number }>,
  targetDist: number,
): { x: number; z: number } {
  let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    const seg = Math.hypot(pts[i]!.x - pts[i - 1]!.x, pts[i]!.z - pts[i - 1]!.z);
    if (acc + seg >= targetDist) {
      const t = (targetDist - acc) / seg;
      return {
        x: pts[i - 1]!.x + (pts[i]!.x - pts[i - 1]!.x) * t,
        z: pts[i - 1]!.z + (pts[i]!.z - pts[i - 1]!.z) * t,
      };
    }
    acc += seg;
  }
  return pts[pts.length - 1]!;
}

/**
 * Append an axis-aligned cuboid centered on (cx, cz) with square footprint `size`, vertical
 * extent [yBottom, yTop]. Adds 24 vertices (4 per face × 6 faces) so each face has its own
 * normal — important for the flat-shaded concrete look.
 */
function appendCuboid(
  positions: number[],
  indices: number[],
  normals: number[],
  uvs: number[],
  cx: number,
  cz: number,
  yBottom: number,
  yTop: number,
  size: number,
): void {
  const half = size * 0.5;
  const x0 = cx - half;
  const x1 = cx + half;
  const z0 = cz - half;
  const z1 = cz + half;

  // Each face: 4 verts in CCW order viewed from outside, plus the face normal.
  const faces: Array<{
    verts: Array<[number, number, number]>;
    normal: [number, number, number];
  }> = [
    // +X
    { verts: [[x1, yBottom, z0], [x1, yBottom, z1], [x1, yTop, z1], [x1, yTop, z0]], normal: [1, 0, 0] },
    // -X
    { verts: [[x0, yBottom, z1], [x0, yBottom, z0], [x0, yTop, z0], [x0, yTop, z1]], normal: [-1, 0, 0] },
    // +Z
    { verts: [[x1, yBottom, z1], [x0, yBottom, z1], [x0, yTop, z1], [x1, yTop, z1]], normal: [0, 0, 1] },
    // -Z
    { verts: [[x0, yBottom, z0], [x1, yBottom, z0], [x1, yTop, z0], [x0, yTop, z0]], normal: [0, 0, -1] },
    // +Y (top)
    { verts: [[x0, yTop, z0], [x1, yTop, z0], [x1, yTop, z1], [x0, yTop, z1]], normal: [0, 1, 0] },
    // -Y (bottom — unlikely to be visible but cheap to emit)
    { verts: [[x0, yBottom, z1], [x1, yBottom, z1], [x1, yBottom, z0], [x0, yBottom, z0]], normal: [0, -1, 0] },
  ];

  for (const face of faces) {
    const base = positions.length / 3;
    for (const [vx, vy, vz] of face.verts) {
      positions.push(vx, vy, vz);
      normals.push(face.normal[0], face.normal[1], face.normal[2]);
      uvs.push(0, 0);
    }
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}
